import React from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import { useAuth } from '@clerk/clerk-react'
import { ConvexProviderWithClerk } from 'convex/react-clerk'
import { convex } from './lib/convex'
import { ToastProvider } from './contexts/ToastContext'
import { UIProvider } from './contexts/UIContext'
import HomePage from './pages/HomePage'
import StorefrontPage from './pages/StorefrontPage'
import ProfilePage from './pages/ProfilePage'
import EditorPage from './pages/EditorPage'
import BookPage from './pages/BookPage'
import ReadingPage from './pages/ReadingPage'
import GutenbergImportPage from './pages/GutenbergImportPage'
import LibraryPage from './pages/LibraryPage'
import AdminDashboardPage from './pages/AdminDashboardPage'
import NotificationsPage from './pages/NotificationsPage'
import UserProfilePage from './pages/UserProfilePage'
import TermsPage from './pages/TermsPage'
import PrivacyPage from './pages/PrivacyPage'
import Header from './components/layout/Header'
import AuthModal from './components/auth/AuthModal'
import ProtectedRoute from './components/auth/ProtectedRoute'
import Toast from './components/ui/Toast'
import './App.css'

/** Redirect signed-in users straight to the storefront; show landing page otherwise. */
const HomeRedirect: React.FC = () => {
  const { isSignedIn, isLoaded } = useAuth()
  if (!isLoaded) return null
  if (isSignedIn) return <Navigate to="/storefront" replace />
  return <HomePage />
}

function App() {
  return (
    <ConvexProviderWithClerk client={convex} useAuth={useAuth}>
      <ToastProvider>
        <UIProvider>
          <Router>
          <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
            <Header />
            <main>
              <Routes>
                <Route path="/" element={<HomeRedirect />} />
                <Route path="/storefront" element={<StorefrontPage />} />
                <Route path="/reading/:branchId" element={<ReadingPage />} />
                <Route
                  path="/import/gutenberg"
                  element={
                    <ProtectedRoute requireRole="admin">
                      <GutenbergImportPage />
                    </ProtectedRoute>
                  }
                />
                <Route path="/branches/:bookId" element={<BookPage />} />
                <Route 
                  path="/editor/:branchId"
                  element={
                    <ProtectedRoute>
                      <EditorPage />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/editor"
                  element={
                    <ProtectedRoute>
                      <EditorPage />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/profile" 
                  element={
                    <ProtectedRoute>
                      <ProfilePage />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/library" 
                  element={
                    <ProtectedRoute>
                      <LibraryPage />
                    </ProtectedRoute>
                  } 
                />
                <Route
                  path="/admin"
                  element={
                    <ProtectedRoute requireRole="admin">
                      <AdminDashboardPage />
                    </ProtectedRoute>
                  }
                />
                <Route 
                  path="/purchases" 
                  element={
                    <ProtectedRoute>
                      <div className="p-8 text-center">Purchase History (Coming in Week 5)</div>
                    </ProtectedRoute>
                  } 
                />
                <Route
                  path="/notifications"
                  element={
                    <ProtectedRoute>
                      <NotificationsPage />
                    </ProtectedRoute>
                  }
                />
                <Route path="/user/:username" element={<UserProfilePage />} />
                <Route path="/terms" element={<TermsPage />} />
                <Route path="/privacy" element={<PrivacyPage />} />
              </Routes>
            </main>
            
            {/* Global Modals */}
            <AuthModal />
            
            {/* Toast Notifications */}
            <Toast />
          </div>
          </Router>
        </UIProvider>
      </ToastProvider>
    </ConvexProviderWithClerk>
  )
}

export default App